import { useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Settings, Globe, Bell, Shield, Key, Webhook, BarChart3, Puzzle, CreditCard, HelpCircle, Rocket, Mail, Sparkles } from 'lucide-react';
import { settingsApi } from '../../api/endpoints/settings';
import { Tabs } from '../../components/ui/Tabs';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { usePermissions } from '../../hooks/usePermissions';
import { GeneralTab } from './tabs/GeneralTab';
import { DomainTab } from './tabs/DomainTab';
import { NotificationsTab } from './tabs/NotificationsTab';
import { EmailBrandingTab } from './tabs/EmailBrandingTab';
import { ClientAuthTab } from './tabs/ClientAuthTab';
import { ApiKeysTab } from './tabs/ApiKeysTab';
import { WebhooksTab } from './tabs/WebhooksTab';
import { TrackingTab } from './tabs/TrackingTab';
import { AppsTab } from './tabs/AppsTab';
import { AiConnectorsTab } from './tabs/AiConnectorsTab';
import { PaymentMethodsTab } from './tabs/PaymentMethodsTab';
import { HelpTab } from './tabs/HelpTab';
import { WelcomeTab } from './tabs/WelcomeTab';
import type { StoreSettings } from '../../types/settings';

export function SettingsPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();
  const { can } = usePermissions();
  // Tab persists in the URL so /settings?tab=domain deep-links into the domain
  // tab. The bot's navigate_to relies on this for fine-grained jumps.
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'general';
  const setActiveTab = (key: string) => setSearchParams({ tab: key }, { replace: true });

  const { data, isLoading } = useQuery({
    queryKey: ['settings', storeId],
    queryFn: () => settingsApi.get(storeId!),
    enabled: !!storeId,
  });

  // Discord-verified merchants have no API access (the backend refuses every
  // /api-keys route for them), so the tab is hidden rather than left to 403.
  const hideApiTab = user?.discordLinked === true;

  const tabs = [
    // Each tab requires the SAME permission its backend write endpoint enforces
    // (see code/routes/api/v1/dashboard/settings.js). A merchant granted the
    // whole 'settings' section gets 'settings.*' (matches all of these); a
    // granular 'settings.edit' grant now correctly reveals only the generic
    // settings tabs, not domain/payments/notifications/auth.
    { key: 'general', label: t('settings.general'), icon: <Settings className="h-4 w-4" />, permission: 'info.edit' },
    { key: 'domain', label: t('settings.domain'), icon: <Globe className="h-4 w-4" />, permission: 'settings.editDomain' },
    { key: 'paymentMethods', label: t('settings.paymentMethods'), icon: <CreditCard className="h-4 w-4" />, permission: 'settings.editPaymentMethods' },
    { key: 'notifications', label: t('settings.notifications'), icon: <Bell className="h-4 w-4" />, permission: 'settings.editNotifications' },
    { key: 'emailBranding', label: t('settings.emailBranding', 'Email branding'), icon: <Mail className="h-4 w-4" />, permission: 'settings.edit' },
    { key: 'auth', label: t('settings.clientAuth'), icon: <Shield className="h-4 w-4" />, permission: 'settings.clientsAuthentication' },
    ...(hideApiTab ? [] : [{ key: 'apiKeys', label: t('settings.apiKeys'), icon: <Key className="h-4 w-4" />, permission: 'settings.edit' }]),
    { key: 'aiConnectors', label: t('settingsAiConnectors.tab', 'AI connectors'), icon: <Sparkles className="h-4 w-4" />, permission: 'settings.edit' },
    { key: 'webhooks', label: t('settingsWebhooks.title'), icon: <Webhook className="h-4 w-4" />, permission: 'settings.edit' },
    { key: 'tracking', label: t('settingsTracking.title'), icon: <BarChart3 className="h-4 w-4" />, permission: 'settings.edit' },
    { key: 'apps', label: t('sidebar.apps', 'Apps'), icon: <Puzzle className="h-4 w-4" />, permission: 'settings.edit' },
    { key: 'welcome', label: t('settingsWelcome.tab', 'Welcome'), icon: <Rocket className="h-4 w-4" /> },
    { key: 'help', label: t('settingsHelp.tab', 'Help'), icon: <HelpCircle className="h-4 w-4" /> },
  ];

  const visibleTabs = useMemo(
    () => tabs.filter(t => !t.permission || can(t.permission)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [can, t],
  );

  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.some(tab => tab.key === activeTab)) {
      setActiveTab(visibleTabs[0]?.key ?? 'general');
    }
  }, [visibleTabs, activeTab]);

  if (isLoading) return <LoadingSpinner fullPage />;

  const settings = data?.settings as StoreSettings | undefined;

  return (
    <div>
      <h1 className="text-xl font-bold mb-6">{t('sidebar.settings')}</h1>
      <Tabs tabs={visibleTabs} activeTab={activeTab} onChange={setActiveTab} className="mb-6 flex-wrap" />
      <div className="mt-6">
        {activeTab === 'general' && <GeneralTab settings={settings} storeId={storeId!} />}
        {activeTab === 'domain' && <DomainTab settings={settings} storeId={storeId!} />}
        {activeTab === 'paymentMethods' && <PaymentMethodsTab settings={settings} storeId={storeId!} />}
        {activeTab === 'notifications' && <NotificationsTab settings={settings} storeId={storeId!} />}
        {activeTab === 'emailBranding' && <EmailBrandingTab settings={settings} storeId={storeId!} />}
        {activeTab === 'auth' && <ClientAuthTab settings={settings} storeId={storeId!} />}
        {activeTab === 'apiKeys' && <ApiKeysTab storeId={storeId!} />}
        {activeTab === 'aiConnectors' && <AiConnectorsTab storeId={storeId!} />}
        {activeTab === 'webhooks' && <WebhooksTab settings={settings} storeId={storeId!} />}
        {activeTab === 'tracking' && <TrackingTab settings={settings} storeId={storeId!} />}
        {activeTab === 'apps' && <AppsTab storeId={storeId!} />}
        {activeTab === 'welcome' && <WelcomeTab settings={settings} storeId={storeId!} />}
        {activeTab === 'help' && <HelpTab />}
      </div>
    </div>
  );
}
